import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Plus, Trash2, Settings as SettingsIcon, Users, Activity, Save, RefreshCw, Info, UserCheck, Shield, ChevronRight, Download, Database, Loader2, Edit, Palette, FileCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const Settings = () => {
  const { user, hasPermission } = useContext(AuthContext);
  const { ministries, refreshMinistries, currentMinistry } = useMinistry();
  const navigate = useNavigate();
  const [customFields, setCustomFields] = useState([]);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newField, setNewField] = useState({
    field_name: '',
    field_type: 'text',
    options: []
  });
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'volunteer'
  });
  
  // Engagement criteria state
  const [engagementCriteria, setEngagementCriteria] = useState({
    active_rate_threshold: 30,
    recent_events_count: 3,
    disengaged_weeks: 4,
    inactive_weeks: 8,
    min_yearly_attendance: 5
  });
  const [savingCriteria, setSavingCriteria] = useState(false);
  const [updatingEngagement, setUpdatingEngagement] = useState(false);
  
  // Backup states
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [creatingSourceBackup, setCreatingSourceBackup] = useState(false);
  const [backupReady, setBackupReady] = useState(null);
  const [sourceBackupReady, setSourceBackupReady] = useState(null);
  
  // Ministry management states
  const [ministryDialogOpen, setMinistryDialogOpen] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState(null);
  const [newMinistry, setNewMinistry] = useState({
    name: '',
    singular_name: '',
    description: '',
    color: '#0ea5e9'
  });
  const [savingMinistry, setSavingMinistry] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCustomFields();
      fetchEngagementCriteria();
    }
  }, [user]);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API}/custom-fields`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchEngagementCriteria = async () => {
    try {
      const response = await axios.get(`${API}/settings/engagement-criteria`);
      setEngagementCriteria({
        active_rate_threshold: response.data.active_rate_threshold ?? 30,
        recent_events_count: response.data.recent_events_count ?? 3,
        disengaged_weeks: response.data.disengaged_weeks ?? 4,
        inactive_weeks: response.data.inactive_weeks ?? 8,
        min_yearly_attendance: response.data.min_yearly_attendance ?? 5
      });
    } catch (error) {
      console.error('Error fetching engagement criteria:', error);
    }
  };

  const handleSaveEngagementCriteria = async () => {
    // Validation
    if (engagementCriteria.disengaged_weeks >= engagementCriteria.inactive_weeks) {
      toast.error('Semanas para desengajado deve ser menor que semanas para inativo');
      return;
    }
    
    setSavingCriteria(true);
    try {
      await axios.put(`${API}/settings/engagement-criteria`, engagementCriteria);
      toast.success('Critérios de engajamento salvos com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar critérios');
    } finally {
      setSavingCriteria(false);
    }
  };

  const handleUpdateEngagement = async () => {
    setUpdatingEngagement(true);
    try {
      const response = await axios.post(`${API}/reports/update-engagement`);
      toast.success(`Engajamento atualizado! Ativos: ${response.data.stats.ativo}, Desengajados: ${response.data.stats.desengajado}, Inativos: ${response.data.stats.inativo}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar engajamento');
    } finally {
      setUpdatingEngagement(false);
    }
  };

  // Backup de dados (banco de dados)
  const handleCreateDataBackup = async () => {
    setCreatingBackup(true);
    setBackupReady(null);
    try {
      toast.info('Criando backup dos dados...');
      const response = await axios.post(`${API}/backup/create`);
      setBackupReady(response.data);
      toast.success('Backup dos dados criado! Iniciando download...');
      
      // Download via backend endpoint (mais confiável)
      const downloadResponse = await axios.get(`${API}/backup/download/${response.data.filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Backup error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  // Backup de código fonte
  const handleCreateSourceBackup = async () => {
    setCreatingSourceBackup(true);
    setSourceBackupReady(null);
    try {
      toast.info('Criando backup do código fonte... Isso pode levar alguns segundos.');
      const response = await axios.post(`${API}/backup/create-source`);
      setSourceBackupReady(response.data);
      toast.success('Backup do código criado! Iniciando download...');
      
      // Download via backend endpoint (mais confiável)
      const downloadResponse = await axios.get(`${API}/backup/download/${response.data.filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Source backup error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar backup do código');
    } finally {
      setCreatingSourceBackup(false);
    }
  };

  const handleDownloadBackup = async (backupInfo) => {
    if (backupInfo) {
      try {
        toast.info('Iniciando download...');
        const downloadResponse = await axios.get(`${API}/backup/download/${backupInfo.filename}`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = backupInfo.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Download iniciado!');
      } catch (error) {
        toast.error('Erro ao baixar arquivo. Tente criar um novo backup.');
      }
    }
  };

  // Ministry management functions
  const handleCreateMinistry = async (e) => {
    e.preventDefault();
    setSavingMinistry(true);
    try {
      await axios.post(`${API}/ministries`, newMinistry);
      toast.success('Base de dados criada com sucesso!');
      setMinistryDialogOpen(false);
      setNewMinistry({ name: '', singular_name: '', description: '', color: '#0ea5e9' });
      refreshMinistries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar base de dados');
    } finally {
      setSavingMinistry(false);
    }
  };

  const handleUpdateMinistry = async (e) => {
    e.preventDefault();
    if (!editingMinistry) return;
    setSavingMinistry(true);
    try {
      await axios.put(`${API}/ministries/${editingMinistry.id}`, newMinistry);
      toast.success('Base de dados atualizada!');
      setMinistryDialogOpen(false);
      setEditingMinistry(null);
      setNewMinistry({ name: '', singular_name: '', description: '', color: '#0ea5e9' });
      refreshMinistries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar base de dados');
    } finally {
      setSavingMinistry(false);
    }
  };

  const handleDeleteMinistry = async (ministryId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta base de dados? Esta ação não pode ser desfeita.')) {
      return;
    }
    try {
      await axios.delete(`${API}/ministries/${ministryId}`);
      toast.success('Base de dados excluída!');
      refreshMinistries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir base de dados');
    }
  };

  const openEditMinistry = (ministry) => {
    setEditingMinistry(ministry);
    setNewMinistry({
      name: ministry.name,
      singular_name: ministry.singular_name,
      description: ministry.description || '',
      color: ministry.color || '#0ea5e9'
    });
    setMinistryDialogOpen(true);
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/custom-fields`, newField);
      toast.success('Campo personalizado criado!');
      setFieldDialogOpen(false);
      setNewField({ field_name: '', field_type: 'text', options: [] });
      fetchCustomFields();
    } catch (error) {
      toast.error('Erro ao criar campo');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (window.confirm('Tem certeza que deseja excluir este campo?')) {
      try {
        await axios.delete(`${API}/custom-fields/${fieldId}`);
        toast.success('Campo excluído!');
        fetchCustomFields();
      } catch (error) {
        toast.error('Erro ao excluir campo');
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success('Usuário criado com sucesso!');
      setUserDialogOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'volunteer' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="fade-in" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>Configurações</h1>
          <p className="text-lg text-gray-600">Gerencie critérios de engajamento, campos personalizados, usuários e permissões</p>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Revisão de Gênero */}
          {hasPermission('gender_review.view') && (
            <button
              onClick={() => navigate('/gender-review')}
              className="card p-5 text-left hover:shadow-lg transition-all hover:scale-[1.02] group"
              data-testid="goto-gender-review"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-rose-200 flex items-center justify-center">
                    <UserCheck size={24} className="text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Revisão de Gênero</h3>
                    <p className="text-sm text-gray-500">Corrigir gênero dos adolescentes</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-pink-500 transition" />
              </div>
            </button>
          )}
          
          {/* Usuários */}
          {hasPermission('users.view') && (
            <button
              onClick={() => navigate('/users')}
              className="card p-5 text-left hover:shadow-lg transition-all hover:scale-[1.02] group"
              data-testid="goto-users"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-blue-200 flex items-center justify-center">
                    <Shield size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Usuários</h3>
                    <p className="text-sm text-gray-500">Gerenciar usuários do sistema</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-indigo-500 transition" />
              </div>
            </button>
          )}
          
          {/* Permissões */}
          {hasPermission('permissions.manage') && (
            <button
              onClick={() => navigate('/permissions')}
              className="card p-5 text-left hover:shadow-lg transition-all hover:scale-[1.02] group"
              data-testid="goto-permissions"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center">
                    <Users size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Permissões</h3>
                    <p className="text-sm text-gray-500">Configurar perfis e permissões</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-amber-500 transition" />
              </div>
            </button>
          )}
        </div>

        {/* Label Settings Section */}
        <div className="card mb-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-cyan-200 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-sky-600"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect width="12" height="8" x="6" y="14"></rect></svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Etiquetas de Check-in</h2>
                <p className="text-sm text-gray-500">Configure a logo e preferências de impressão</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Logo da Etiqueta</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  {localStorage.getItem('labelLogo') ? (
                    <img 
                      src={localStorage.getItem('labelLogo')} 
                      alt="Logo" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-gray-400 text-xs text-center">Sem logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    id="logo-upload"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          localStorage.setItem('labelLogo', event.target.result);
                          toast.success('Logo atualizada!');
                          window.location.reload();
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    Carregar Logo
                  </Button>
                  {localStorage.getItem('labelLogo') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => {
                        localStorage.removeItem('labelLogo');
                        toast.success('Logo removida!');
                        window.location.reload();
                      }}
                    >
                      Remover
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">Recomendado: imagem quadrada, máximo 200x200px</p>
            </div>

            {/* Auto Print Setting */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Impressão Automática</Label>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto-print"
                  checked={localStorage.getItem('autoPrintLabel') !== 'false'}
                  onChange={(e) => {
                    localStorage.setItem('autoPrintLabel', e.target.checked.toString());
                    toast.success(e.target.checked ? 'Impressão automática ativada' : 'Impressão automática desativada');
                  }}
                  className="w-5 h-5"
                />
                <label htmlFor="auto-print" className="text-sm text-gray-700 cursor-pointer">
                  Abrir janela de impressão automaticamente após check-in
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Quando ativado, a etiqueta será preparada para impressão logo após cada check-in
              </p>
            </div>
          </div>
        </div>

        {/* Engagement Criteria Section */}
        <div className="card mb-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-100 to-emerald-200 flex items-center justify-center">
                <Activity size={24} className="text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Critérios de Engajamento</h2>
                <p className="text-sm text-gray-500">Configure como classificar adolescentes como Ativo, Desengajado ou Inativo</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Active Rate Threshold */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Taxa mínima para Ativo (%)</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Percentual mínimo de presença em relação ao total de eventos para ser considerado Ativo</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                type="number"
                min="0"
                max="100"
                value={engagementCriteria.active_rate_threshold}
                onChange={(e) => setEngagementCriteria({...engagementCriteria, active_rate_threshold: parseInt(e.target.value) || 0})}
                className="w-full"
                data-testid="active-rate-input"
              />
              <p className="text-xs text-gray-500">Adolescentes com taxa ≥ {engagementCriteria.active_rate_threshold}% são Ativos</p>
            </div>

            {/* Recent Events Count */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Eventos recentes a considerar</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Quantidade de eventos recentes. Presença em 1 deles = Ativo</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                type="number"
                min="1"
                max="20"
                value={engagementCriteria.recent_events_count}
                onChange={(e) => setEngagementCriteria({...engagementCriteria, recent_events_count: parseInt(e.target.value) || 1})}
                className="w-full"
                data-testid="recent-events-input"
              />
              <p className="text-xs text-gray-500">Presença em 1 dos últimos {engagementCriteria.recent_events_count} eventos = Ativo</p>
            </div>

            {/* Min Yearly Attendance */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Mínimo de presenças no ano</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Número mínimo de presenças no ano atual para não ser considerado Desengajado</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                type="number"
                min="1"
                max="100"
                value={engagementCriteria.min_yearly_attendance}
                onChange={(e) => setEngagementCriteria({...engagementCriteria, min_yearly_attendance: parseInt(e.target.value) || 1})}
                className="w-full"
                data-testid="min-yearly-input"
              />
              <p className="text-xs text-gray-500">Menos de {engagementCriteria.min_yearly_attendance} presenças no ano = Desengajado</p>
            </div>

            {/* Disengaged Weeks */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Semanas para Desengajado</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Semanas sem presença desde o último evento para ser considerado Desengajado</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                type="number"
                min="1"
                max="51"
                value={engagementCriteria.disengaged_weeks}
                onChange={(e) => setEngagementCriteria({...engagementCriteria, disengaged_weeks: parseInt(e.target.value) || 1})}
                className="w-full"
                data-testid="disengaged-weeks-input"
              />
              <p className="text-xs text-gray-500">Ausente há {engagementCriteria.disengaged_weeks}+ semanas = Desengajado</p>
            </div>

            {/* Inactive Weeks */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Semanas para Inativo</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Semanas sem presença desde o último evento para ser considerado Inativo</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                type="number"
                min="2"
                max="52"
                value={engagementCriteria.inactive_weeks}
                onChange={(e) => setEngagementCriteria({...engagementCriteria, inactive_weeks: parseInt(e.target.value) || 2})}
                className="w-full"
                data-testid="inactive-weeks-input"
              />
              <p className="text-xs text-gray-500">Ausente há {engagementCriteria.inactive_weeks}+ semanas = Inativo</p>
            </div>
          </div>

          {/* Summary Box */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">📋 Resumo dos Critérios</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start space-x-2">
                <span className="w-3 h-3 rounded-full bg-green-500 mt-1 flex-shrink-0"></span>
                <div>
                  <p className="font-medium text-green-700">Ativo</p>
                  <p className="text-gray-600">Taxa ≥ {engagementCriteria.active_rate_threshold}% OU presença nos últimos {engagementCriteria.recent_events_count} eventos</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="w-3 h-3 rounded-full bg-amber-500 mt-1 flex-shrink-0"></span>
                <div>
                  <p className="font-medium text-amber-700">Desengajado</p>
                  <p className="text-gray-600">Ausente há {engagementCriteria.disengaged_weeks}-{engagementCriteria.inactive_weeks - 1} semanas OU &lt; {engagementCriteria.min_yearly_attendance} presenças no ano</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="w-3 h-3 rounded-full bg-red-500 mt-1 flex-shrink-0"></span>
                <div>
                  <p className="font-medium text-red-700">Inativo</p>
                  <p className="text-gray-600">Nunca participou OU ausente há {engagementCriteria.inactive_weeks}+ semanas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleSaveEngagementCriteria} 
              className="btn-primary flex items-center space-x-2"
              disabled={savingCriteria}
              data-testid="save-criteria-button"
            >
              <Save size={18} />
              <span>{savingCriteria ? 'Salvando...' : 'Salvar Critérios'}</span>
            </Button>
            <Button 
              onClick={handleUpdateEngagement} 
              variant="outline"
              className="flex items-center space-x-2"
              disabled={updatingEngagement}
              data-testid="update-engagement-button"
            >
              <RefreshCw size={18} className={updatingEngagement ? 'animate-spin' : ''} />
              <span>{updatingEngagement ? 'Recalculando...' : 'Recalcular Engajamento Agora'}</span>
            </Button>
          </div>
        </div>

        {/* Custom Fields Section */}
        <div className="card mb-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <SettingsIcon size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Campos Personalizados</h2>
                <p className="text-sm text-gray-500">Adicione campos extras ao cadastro</p>
              </div>
            </div>
            
            <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex items-center space-x-2" data-testid="add-field-button">
                  <Plus size={20} />
                  <span>Novo Campo</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Campo Personalizado</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateField} className="space-y-4" data-testid="field-form">
                  <div>
                    <Label>Nome do Campo</Label>
                    <Input
                      value={newField.field_name}
                      onChange={(e) => setNewField({...newField, field_name: e.target.value})}
                      required
                      placeholder="Ex: Endereço, Escola, etc."
                      data-testid="field-name-input"
                    />
                  </div>
                  <div>
                    <Label>Tipo de Campo</Label>
                    <Select value={newField.field_type} onValueChange={(value) => setNewField({...newField, field_type: value})}>
                      <SelectTrigger data-testid="field-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Texto</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="select">Seleção</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="outline" onClick={() => setFieldDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="btn-primary" data-testid="field-submit-button">Criar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {customFields.length > 0 ? (
            <div className="space-y-3">
              {customFields.map((field) => (
                <div key={field.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid="custom-field-item">
                  <div>
                    <p className="font-semibold text-gray-900">{field.field_name}</p>
                    <p className="text-sm text-gray-500 capitalize">Tipo: {field.field_type}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteField(field.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition"
                    data-testid="delete-field-button"
                  >
                    <Trash2 size={18} className="text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">Nenhum campo personalizado criado</p>
          )}
        </div>

        {/* Users Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                <Users size={24} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Gerenciar Usuários</h2>
                <p className="text-sm text-gray-500">Adicione administradores e voluntários</p>
              </div>
            </div>
            
            <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex items-center space-x-2" data-testid="add-user-button">
                  <Plus size={20} />
                  <span>Novo Usuário</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Novo Usuário</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4" data-testid="user-form">
                  <div>
                    <Label>Nome Completo</Label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                  <div>
                    <Label>Senha</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                  <div>
                    <Label>Função</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="volunteer">Voluntário</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="btn-primary" data-testid="user-submit-button">Criar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 text-center">
            <Users size={48} className="mx-auto mb-3 text-purple-500" />
            <p className="text-gray-600">Use o botão acima para adicionar novos usuários ao sistema</p>
            <p className="text-sm text-gray-500 mt-2">Administradores têm acesso total, voluntários podem fazer check-in e visualizar dados</p>
          </div>
        </div>

        {/* Ministry/Database Management Section - Admin Only */}
        {user?.role === 'admin' && (
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-violet-200 flex items-center justify-center">
                  <Database size={24} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Bases de Dados</h2>
                  <p className="text-sm text-gray-500">Gerencie diferentes ministérios (Adolescentes, Pré-Adolescentes, etc.)</p>
                </div>
              </div>
              
              <Dialog open={ministryDialogOpen} onOpenChange={(open) => {
                setMinistryDialogOpen(open);
                if (!open) {
                  setEditingMinistry(null);
                  setNewMinistry({ name: '', singular_name: '', description: '', color: '#0ea5e9' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary flex items-center space-x-2">
                    <Plus size={20} />
                    <span>Nova Base</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingMinistry ? 'Editar Base de Dados' : 'Nova Base de Dados'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={editingMinistry ? handleUpdateMinistry : handleCreateMinistry} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="ministry-name">Nome (plural)</Label>
                      <Input
                        id="ministry-name"
                        placeholder="Ex: Pré-Adolescentes"
                        value={newMinistry.name}
                        onChange={(e) => setNewMinistry({...newMinistry, name: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Nome que aparece no menu e títulos</p>
                    </div>
                    <div>
                      <Label htmlFor="ministry-singular">Nome (singular)</Label>
                      <Input
                        id="ministry-singular"
                        placeholder="Ex: Pré-Adolescente"
                        value={newMinistry.singular_name}
                        onChange={(e) => setNewMinistry({...newMinistry, singular_name: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Nome usado em formulários (Novo Pré-Adolescente)</p>
                    </div>
                    <div>
                      <Label htmlFor="ministry-desc">Descrição (opcional)</Label>
                      <Input
                        id="ministry-desc"
                        placeholder="Ex: Ministério de Pré-Adolescentes de 9 a 11 anos"
                        value={newMinistry.description}
                        onChange={(e) => setNewMinistry({...newMinistry, description: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ministry-color">Cor de identificação</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <input
                          type="color"
                          id="ministry-color"
                          value={newMinistry.color}
                          onChange={(e) => setNewMinistry({...newMinistry, color: e.target.value})}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <div className="flex gap-2">
                          {['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899'].map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setNewMinistry({...newMinistry, color})}
                              className={`w-8 h-8 rounded-lg border-2 transition ${newMinistry.color === color ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setMinistryDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="btn-primary" disabled={savingMinistry}>
                        {savingMinistry ? <Loader2 className="animate-spin" size={18} /> : (editingMinistry ? 'Salvar' : 'Criar')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* List of Ministries */}
            <div className="space-y-3">
              {ministries.map((ministry) => (
                <div
                  key={ministry.id}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition ${
                    currentMinistry?.id === ministry.id ? 'border-sky-300 bg-sky-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: ministry.color || '#0ea5e9' }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">{ministry.name}</p>
                        {ministry.is_default && (
                          <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Padrão</span>
                        )}
                        {currentMinistry?.id === ministry.id && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Ativo</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{ministry.description || `Singular: ${ministry.singular_name}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditMinistry(ministry)}
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Edit size={16} />
                    </Button>
                    {!ministry.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMinistry(ministry.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              {ministries.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Database size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Nenhuma base de dados cadastrada</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Backup Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-200 flex items-center justify-center">
                <Database size={24} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Backup do Sistema</h2>
                <p className="text-sm text-gray-500">Baixe os dados e código fonte do sistema</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Backup de Dados */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-5">
              <div className="flex items-center space-x-2 mb-3">
                <Database size={20} className="text-emerald-600" />
                <h3 className="font-semibold text-gray-800">Dados do Sistema</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 mb-4">
                <li>✓ Adolescentes e check-ins</li>
                <li>✓ Voluntários, eventos e PGs</li>
                <li>✓ Áreas de serviço e configurações</li>
                <li>✓ Script de restauração Python</li>
              </ul>
              
              <Button 
                onClick={handleCreateDataBackup} 
                className="w-full btn-primary flex items-center justify-center space-x-2"
                disabled={creatingBackup}
                data-testid="create-data-backup-button"
              >
                {creatingBackup ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Baixando...</span>
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    <span>Baixar Dados</span>
                  </>
                )}
              </Button>
              
              {backupReady && (
                <div className="mt-3 p-2 bg-white rounded border border-emerald-200">
                  <p className="text-xs text-emerald-700 font-medium">
                    ✅ {backupReady.filename} ({backupReady.file_size_mb} MB)
                  </p>
                </div>
              )}
            </div>

            {/* Backup de Código */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5">
              <div className="flex items-center space-x-2 mb-3">
                <FileCode size={20} className="text-blue-600" />
                <h3 className="font-semibold text-gray-800">Código Fonte</h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 mb-4">
                <li>✓ Backend (FastAPI/Python)</li>
                <li>✓ Frontend (React)</li>
                <li>✓ Arquivos de configuração</li>
                <li>✓ Guia de instalação</li>
              </ul>
              
              <Button 
                onClick={handleCreateSourceBackup} 
                variant="outline"
                className="w-full border-blue-500 text-blue-700 hover:bg-blue-50 flex items-center justify-center space-x-2"
                disabled={creatingSourceBackup}
                data-testid="create-source-backup-button"
              >
                {creatingSourceBackup ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Baixando...</span>
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    <span>Baixar Código</span>
                  </>
                )}
              </Button>
              
              {sourceBackupReady && (
                <div className="mt-3 p-2 bg-white rounded border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium">
                    ✅ {sourceBackupReady.filename} ({sourceBackupReady.file_size_mb} MB)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Settings;
